/**
 * Deterministic, seeded pseudo-random number generator.
 *
 * Reproducibility is a hard requirement: the same (seed) always yields the
 * same sequence, on any platform, so a question can be regenerated from its
 * seed for debugging, replay, or shareable quizzes. Uses a Mulberry32 core
 * seeded through a string hash, all in 32-bit integer math - no reliance on
 * Math.random and no platform-dependent behavior.
 */

/** Bump when the algorithm changes in a way that alters sequences. */
export const RNG_VERSION = 1;

/** Hash an arbitrary string seed to a 32-bit unsigned integer (FNV-1a). */
export function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    // 32-bit FNV prime multiply via shifts to stay in integer range.
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export class Rng {
  private state: number;
  /** The original seed, retained so a question can record what produced it. */
  readonly seed: string;

  constructor(seed: string | number) {
    this.seed = typeof seed === "number" ? seed.toString() : seed;
    this.state = typeof seed === "number" ? seed >>> 0 : hashSeed(seed);
    // Avoid a zero state, which weakens the first outputs.
    if (this.state === 0) this.state = 0x9e3779b9;
  }

  /** Next float in [0, 1). Mulberry32. */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) | 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    if (min > max) throw new RangeError(`int: min ${min} > max ${max}`);
    return min + Math.floor(this.next() * (max - min + 1));
  }

  /** Integer in [min, max] inclusive, excluding zero. Throws if impossible. */
  intNonZero(min: number, max: number): number {
    if (min > max) throw new RangeError(`intNonZero: min ${min} > max ${max}`);
    if (min === 0 && max === 0) {
      throw new RangeError("intNonZero: range is only {0}");
    }
    let v = this.int(min, max);
    while (v === 0) v = this.int(min, max);
    return v;
  }

  /** True with the given probability (default 0.5). */
  bool(p = 0.5): boolean {
    return this.next() < p;
  }

  /** +1 or -1. `pNegative` biases toward -1. */
  sign(pNegative = 0.5): 1 | -1 {
    return this.next() < pNegative ? -1 : 1;
  }

  /** Uniformly pick one element. Throws on an empty array. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new RangeError("pick: empty array");
    return items[this.int(0, items.length - 1)] as T;
  }

  /**
   * Weighted pick. `weights` must align with `items` and contain
   * non-negative values with a positive sum.
   */
  weighted<T>(items: readonly T[], weights: readonly number[]): T {
    if (items.length === 0) throw new RangeError("weighted: empty array");
    if (items.length !== weights.length) {
      throw new RangeError("weighted: items/weights length mismatch");
    }
    let total = 0;
    for (const w of weights) {
      if (w < 0) throw new RangeError("weighted: negative weight");
      total += w;
    }
    if (total <= 0) throw new RangeError("weighted: weights sum to zero");
    let r = this.next() * total;
    for (let i = 0; i < items.length; i++) {
      r -= weights[i] as number;
      if (r < 0) return items[i] as T;
    }
    return items[items.length - 1] as T;
  }

  /** In-place Fisher-Yates shuffle of a copy; returns the shuffled copy. */
  shuffle<T>(items: readonly T[]): T[] {
    const a = items.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      [a[i], a[j]] = [a[j] as T, a[i] as T];
    }
    return a;
  }
}

/** Generate a fresh random seed string for a new question. */
export function makeSeed(): string {
  // Combine time and a random component; only used to seed determinism, not
  // for anything security-sensitive.
  const t = Date.now().toString(36);
  const r = Math.floor(Math.random() * 0xffffffff).toString(36);
  return `${t}-${r}`;
}
